import { Injectable } from "@angular/core";
import { AbstractControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { DealsPolicies } from "app/model/types/deals-policies-types.model";
import { OptimizationTypes } from "app/model/types/optimization-types";
import { cloneDeep, merge } from "lodash-es";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { BidMinimumsService } from "app/services/bid-minimums.service";
import { FormValidator } from "../../../../core/form-validator";
import { CampaignsService } from "../campaigns.service";
import { ICampaign } from "../campaigs.types";
import { getTraderIdsFromCampaign } from "../utils/campaign-trader.utils";
import { completeVideoPositions } from "./utils/strategy-targeting.utils";
import { AutoDailyShare, splitAutoDailyShare } from "./utils/campaign-budget.utils";
import { TabErrorService } from "./campaign-detail-error.service";

/**
 * Every `schedule` key MediaSmart declares as `number`.
 *
 * Kept here because getData() is the single place where this form turns into a DSP payload. The
 * canonical list, with the type of every schedule field, lives in the API repo at
 * `docs/integrations/MEDIASMART.md` — the backend normalises against the same one.
 */
const SCHEDULE_NUMERIC_FIELDS = [
    "max_global_cost",
    "max_global_impressions",
    "max_global_clicks",
    "max_daily_cost",
    "max_daily_impressions",
    "max_daily_clicks",
    "max_impressions_user",
    "max_impressions_user_day",
    "max_impressions_user_hour",
    "max_adviewed_user",
    "max_adviewed_user_day",
    "max_adviewed_user_hour",
    "budget_limit_publisher",
];

/**
 * Plan 31 · pricing groups that exist on the CAMPAIGN only (B4). A line item shows the parent's
 * values through mergeWithParent() but never sends them back: the strategy endpoint has no such
 * fields and the DSP payload must not carry them.
 */
/** The pricing inputs emit strings; the API validates numbers and takes null for a blank. */
const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const parsed = typeof value === "number" ? value : parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : null;
};

const CAMPAIGN_PRICING_KEYS = [
    "client_pricing",
    "pricing_plan_id",
    "cost_breakdown",
    "gross_margin_enabled",
    "gross_margin_pct",
    "lifetime_goal_impressions",
    "lifetime_goal_units",
];

@Injectable({
    providedIn: "root",
})
export class FormService {
    public form: UntypedFormGroup;
    public dealsForm: UntypedFormGroup;
    public targetingForm: UntypedFormGroup;
    public retargeting: UntypedFormGroup;
    public scheduleForm: UntypedFormGroup;
    public creativesForm: UntypedFormGroup;
    public audienceForm: UntypedFormGroup;
    public wizardForm: UntypedFormGroup;
    public duplicateForm: UntypedFormGroup;
    public duplicateStrategyForm: UntypedFormGroup;
    public contactUs: UntypedFormGroup;

    public options: any = {};
    private subscriptions: Subscription = new Subscription();
    private originalForm: string;
    private originalFormData: any = null;

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _campaignService: CampaignsService,
        private _campaignErrorService: TabErrorService,
        private _toastr: ToastrService,
        private _bidMinimums: BidMinimumsService
    ) {
        this._generateAllForms();
    }

    private _campaign?: ICampaign;

    /**
     * Bid minimum for a campaign's CPM goal.
     *
     * The campaign's CPM goal is the bid its auto line items follow (the API cascades it to them), so
     * a value under the minimum would either be refused by the DSP or make them bid it. Only checked
     * once the user has touched the field (dirty), so a campaign saved earlier with a lower goal can
     * still be edited in other tabs; and only on a campaign, since a line item's goal is not a price.
     * Reports the standard `min` error so the field paints its usual message.
     */
    private _goalCpmMinimum(control: AbstractControl) {
        const value = control.value;
        if (this._isStrategyContext || !control.dirty || value === null || value === "") {
            return null;
        }
        if (control.parent?.get("kpi")?.value !== "cpm") {
            return null;
        }
        const min = this._bidMinimums.getCpmMinimum();
        return Number(value) < min ? { min: { min, actual: value } } : null;
    }

    /**
     * True while the shared forms hold a line item (strategy) instead of a campaign.
     *
     * The forms are a singleton shared by both screens, but campaign and line item have different
     * budget rules: a line item's lifetime budget is optional and must never be wiped, while a
     * campaign's depends on its budget_type radio. This flag is the only way for form-level code
     * (getData, validators) to tell which of the two it is looking at.
     *
     * It is set from inside the entry points below so no caller has to remember to flip it.
     */
    private _isStrategyContext: boolean = false;

    /**
     * Cómo reparte MediaSmart por día la campaña cargada mientras está en `auto`, o null.
     *
     * Vive fuera de `scheduleForm` a propósito: son cifras que calcula el DSP, no topes del trader, y
     * en el formulario se pintaban y se guardaban como tales (ver `splitAutoDailyShare`). Solo la
     * escribe el contexto de campaña; el de line item la deja como estaba.
     */
    public campaignAutoDailyShare: AutoDailyShare | null = null;

    get campaign() {
        return this._campaign;
    }

    get isStrategyContext(): boolean {
        return this._isStrategyContext;
    }

    set campaign(value: ICampaign) {
        this._campaign = value;
        this._isStrategyContext = false;
        this._fillForm();
    }

    public campaignMergedSTrategy(value: any) {
        this._campaign = value;
        this._isStrategyContext = true;
        this._fillForm();
    }

    /**
     * Prepara el `schedule` de una CAMPAÑA que llega del backend para `scheduleForm`: aparta el
     * reparto automático de MediaSmart a `campaignAutoDailyShare` y devuelve el resto.
     *
     * Por aquí pasan los dos caminos que rellenan el formulario con una campaña —la carga y el
     * refresco tras guardar— y los dos antes de la foto con la que se calcula el diff.
     *
     * `dspProvider` es el de esa misma campaña: en Perion los diarios son topes reales de Hivestack
     * y se quedan en el formulario (ver `isMediaSmartAutoDailyShare`).
     */
    public takeCampaignAutoDailyShare(schedule: any, dspProvider: unknown): any {
        const { schedule: formSchedule, share } = splitAutoDailyShare(schedule, dspProvider);
        this.campaignAutoDailyShare = share;

        return formSchedule;
    }

    public setStrategySubCampaign(value: any) {
        this._campaign = value;
        this._isStrategyContext = true;
        this._fillStrategyForm();
        this.targetingForm.get("publisher_type").setValue({});
    }

    public updateDealsFormForStrategy(strategy: any, parent: any) {
        if (!strategy || !parent) return;

        const mergedData = this.mergeWithParentForDeals(parent, strategy);
        if (mergedData.deals_and_pricing) {
            this.dealsForm.patchValue(mergedData.deals_and_pricing);
        }
    }

    public updateScheduleFormForStrategy(strategy: any, parent: any) {
        if (!strategy || !parent) return;

        const mergedData = this.mergeWithParentForSchedule(parent, strategy);
        if (mergedData.schedule) {
            this.scheduleForm.patchValue(mergedData.schedule);
        }
    }

    public preserveEmptyPricingFields() {
        // This method ensures that empty pricing fields in strategies stay empty
        // and don't get overridden by parent campaign values
        const currentValues = this.dealsForm.getRawValue();

        if (currentValues.cpm === "") {
            this.dealsForm.get("cpm")?.setValue("", { emitEvent: false });
        }
        if (currentValues.cpc === "") {
            this.dealsForm.get("cpc")?.setValue("", { emitEvent: false });
        }
    }

    public preserveEmptyFrequencyLimitFields() {
        // This method ensures that empty frequency limit fields in strategies stay empty
        // and don't get overridden by parent campaign values
        const currentValues = this.scheduleForm.getRawValue();
        const frequencyLimitFields = [
            "max_impressions_user",
            "max_impressions_user_day",
            "max_impressions_user_hour",
            "max_adviewed_user",
            "max_adviewed_user_day",
            "max_adviewed_user_hour",
        ];

        frequencyLimitFields.forEach((field) => {
            if (currentValues[field] === "") {
                this.scheduleForm.get(field)?.setValue("", { emitEvent: false });
            }
        });
    }

    private mergeWithParentForDeals(campaign: any, strategy: any): any {
        if (!campaign) return strategy || {};
        if (!strategy) return campaign || {};

        // For deals and pricing, we want to be more permissive and allow strategy values to override parent values
        // even if they are empty strings or specific values like "0"
        const mergeDealsAndPricing = (parentDeals: any, strategyDeals: any): any => {
            if (!parentDeals) return strategyDeals || {};
            if (!strategyDeals) return parentDeals || {};

            const result = { ...parentDeals };

            // For deals_and_pricing, allow strategy values to override parent values
            // Including empty strings (when user intentionally clears a field)
            // Only fallback to parent if strategy value is null or undefined
            Object.keys(strategyDeals).forEach((key) => {
                const strategyValue = strategyDeals[key];
                // For pricing fields (cpm, cpc), preserve empty strings as user intention
                if (strategyValue !== null && strategyValue !== undefined) {
                    result[key] = strategyValue;
                } else if ((key === "cpm" || key === "cpc") && strategyDeals.hasOwnProperty(key)) {
                    // If the strategy explicitly has the key but it's null/undefined,
                    // this means user intentionally cleared it, so keep it empty
                    result[key] = "";
                }
            });

            return result;
        };

        // Use the general merge for other fields
        const generalMerged = this.mergeWithParent(campaign, strategy);

        // Special handling for deals_and_pricing
        if (strategy.deals_and_pricing || campaign.deals_and_pricing) {
            generalMerged.deals_and_pricing = mergeDealsAndPricing(campaign.deals_and_pricing, strategy.deals_and_pricing);
        }

        return generalMerged;
    }

    private mergeWithParentForSchedule(campaign: any, strategy: any): any {
        if (!campaign) return strategy || {};
        if (!strategy) return campaign || {};

        // For schedule, we want to allow empty values for frequency limit fields
        const mergeSchedule = (parentSchedule: any, strategySchedule: any): any => {
            if (!parentSchedule) return strategySchedule || {};
            if (!strategySchedule) return parentSchedule || {};

            const result = { ...parentSchedule };
            const frequencyLimitFields = [
                "max_impressions_user",
                "max_impressions_user_day",
                "max_impressions_user_hour",
                "max_adviewed_user",
                "max_adviewed_user_day",
                "max_adviewed_user_hour",
            ];

            // For schedule fields, allow strategy values to override parent values
            // For frequency limits, allow empty strings to override parent values
            Object.keys(strategySchedule).forEach((key) => {
                const strategyValue = strategySchedule[key];
                if (frequencyLimitFields.includes(key)) {
                    // For frequency limit fields, allow empty values to override parent values
                    if (strategyValue !== null && strategyValue !== undefined) {
                        result[key] = strategyValue;
                    } else if (strategySchedule.hasOwnProperty(key)) {
                        // If the strategy explicitly has the key but it's null/undefined,
                        // this means user intentionally cleared it, so keep it empty
                        result[key] = "";
                    }
                } else {
                    // For other fields, only override if not null/undefined/empty
                    if (strategyValue !== null && strategyValue !== undefined && strategyValue !== "") {
                        result[key] = strategyValue;
                    }
                }
            });

            return result;
        };

        // Use the general merge for other fields
        const generalMerged = this.mergeWithParent(campaign, strategy);

        // Special handling for schedule
        if (strategy.schedule || campaign.schedule) {
            generalMerged.schedule = mergeSchedule(campaign.schedule, strategy.schedule);
        }

        return generalMerged;
    }

    ngOnInit() {
        this.subscriptions.add(
            this._campaignErrorService.errors$.subscribe((errors) => {
                const hasErrors = errors && Object.values(errors).some((v) => v === true);
                if (hasErrors) {
                    console.warn("[FormService] Campaign validation errors:", errors);
                }
            })
        );
    }

    public getFullForm(): FormGroup {
        const fullForm: FormGroup = this.getData();
        fullForm.disable();
        return this.getData();
    }

    public validateData(isStrategy?: boolean): boolean {
        this.markAllAsTouched();
        this._checkTabErrors(isStrategy);

        return (
            this.form.valid &&
            this.dealsForm.valid &&
            this.targetingForm.valid &&
            this.retargeting.valid &&
            this.scheduleForm.valid &&
            this.creativesForm.valid
        );
    }

    /**
     * Mutates targetingFormData in place into its outbound (getData()) shape:
     * connection/device_type/identifiers/browsers arrays -> {value: true} maps,
     * conditional deletes for traffic_viewability/adstxt_publisher_list, etc.
     * Shared by getData() and getUnsavedTargetingKeys() so both sides of that
     * diff use the same shape — diffing raw form arrays against this
     * post-transform shape otherwise flags these keys as changed on every call.
     */
    private _normalizeTargetingShape(targetingFormData: any): any {
        if (!targetingFormData) {
            return targetingFormData;
        }

        targetingFormData.adstxt_publisher_list = this._normalizeAdsTxtPublisherListValue(
            targetingFormData.adstxt_publisher_list ?? targetingFormData.adstxt_publishers
        );

        if (Array.isArray(targetingFormData.geolist_acquisition)) {
            targetingFormData.geolist_acquisition = targetingFormData.geolist_acquisition[0] || "";
        }

        if (!targetingFormData.traffic_viewability || targetingFormData.traffic_viewability === "") {
            delete targetingFormData.traffic_viewability;
        }

        // Publisher type: only send true values (never web: false or app: false)
        if (targetingFormData.publisher_type && typeof targetingFormData.publisher_type === "object") {
            const pt = targetingFormData.publisher_type as Record<string, boolean>;
            targetingFormData.publisher_type = {};
            if (pt.web === true) targetingFormData.publisher_type.web = true;
            if (pt.app === true) targetingFormData.publisher_type.app = true;
        }

        // If Ads.txt filter isn't enabled, don't send its dependent fields
        if (targetingFormData.mandatory_ads_txt !== true) {
            delete targetingFormData.adstxt_publisher_list;
        }

        for (const field of ["connection", "device_type", "identifiers", "browsers"]) {
            if (Array.isArray(targetingFormData[field])) {
                const asObject: Record<string, boolean> = {};
                targetingFormData[field].forEach((value: string) => {
                    asObject[value] = true;
                });
                targetingFormData[field] = asObject;
            }
        }

        return targetingFormData;
    }

    public getData() {
        const generalFormData = this.form.getRawValue();
        const dealsFormData = this.dealsForm.getRawValue();
        const targetingFormData = this.targetingForm.getRawValue();
        const retargetingFormData = this.retargeting.getRawValue();
        const scheduleFormData = this.scheduleForm.getRawValue();
        const creativesFormData = this.creativesForm.getRawValue();

        this._normalizeTargetingShape(targetingFormData);

        // Preserve Ad Views (CPV with cpv_event: "Ad Views") and CPV Video completion (cpv_event: "videocomplete")
        const isAdViews =
            dealsFormData.cpv !== null &&
            dealsFormData.cpv !== undefined &&
            dealsFormData.cpv !== "" &&
            dealsFormData.cpv_event === "Ad Views";
        const isVideoComplete =
            dealsFormData.cpv !== null &&
            dealsFormData.cpv !== undefined &&
            dealsFormData.cpv !== "" &&
            dealsFormData.cpv_event === "videocomplete";

        // Check if it's a Conversion (CPA with event_number_for_cpa starting with "events")
        const hasConversionEvent =
            dealsFormData.event_number_for_cpa &&
            typeof dealsFormData.event_number_for_cpa === "string" &&
            dealsFormData.event_number_for_cpa.startsWith("events") &&
            ["1", "2", "3", "4", "5"].includes(dealsFormData.event_number_for_cpa.replace("events", ""));

        // Preserve CPA value early if it's a Conversion type (before any cleanup logic)
        let preservedCpaValue: number | null = null;
        if (hasConversionEvent && dealsFormData.cpa !== null && dealsFormData.cpa !== undefined && dealsFormData.cpa !== "") {
            const cpaValue = typeof dealsFormData.cpa === "string" ? parseFloat(dealsFormData.cpa) : Number(dealsFormData.cpa);
            if (!isNaN(cpaValue) && cpaValue > 0) {
                preservedCpaValue = cpaValue;
            }
        }

        // Check if CPA has a valid value (not null, undefined, empty string, or "0" as string)
        const hasCpaValue =
            dealsFormData.cpa !== null &&
            dealsFormData.cpa !== undefined &&
            dealsFormData.cpa !== "" &&
            dealsFormData.cpa !== "0" &&
            parseFloat(String(dealsFormData.cpa)) > 0;

        const isConversion = hasConversionEvent && hasCpaValue;

        if (dealsFormData.optimization_type === OptimizationTypes.OFF) {
            // Don't clear Ad Views or CPV Video completion or Conversion 1-5 if they exist
            if (!isAdViews && !isVideoComplete) {
                dealsFormData.cpv = null;
                dealsFormData.cpv_event = null;
            }
            // Don't clear Conversion if event_number_for_cpa is set (even if cpa value check failed)
            if (!hasConversionEvent) {
                dealsFormData.cpa = null;
                dealsFormData.event_number_for_cpa = null;
            }
        } else if (dealsFormData.optimization_type === OptimizationTypes.CPA) {
            // Don't clear Ad Views or CPV Video completion if they exist
            if (!isAdViews && !isVideoComplete) {
                dealsFormData.cpv = null;
                dealsFormData.cpv_event = null;
            }
            // Don't clear Conversion if event_number_for_cpa is set
            if (!hasConversionEvent) {
                dealsFormData.cpa = null;
                dealsFormData.event_number_for_cpa = null;
            }
        } else if (dealsFormData.optimization_type === OptimizationTypes.CPV) {
            // Don't clear Conversion 1-5 if event_number_for_cpa is set
            if (!hasConversionEvent) {
                dealsFormData.cpa = null;
                dealsFormData.event_number_for_cpa = null;
            }
        }

        if (dealsFormData.deal_policy === DealsPolicies.PLC) {
            dealsFormData.optimization_type = OptimizationTypes.OFF;
            // Don't clear Ad Views or CPV Video completion or Conversion 1-5 if they exist
            if (!isAdViews && !isVideoComplete) {
                dealsFormData.cpv = null;
                dealsFormData.cpv_event = null;
            }
            // Don't clear Conversion if event_number_for_cpa is set
            if (!hasConversionEvent) {
                dealsFormData.cpa = null;
                dealsFormData.event_number_for_cpa = null;
            }
        }

        // Ensure Ad Views is properly formatted
        if (isAdViews) {
            dealsFormData.cpv_event = "Ad Views";
        }

        // Ensure CPV Video completion is properly formatted
        if (isVideoComplete) {
            dealsFormData.cpv_event = "videocomplete";
        }

        // Ensure Conversion 1-5 is properly formatted and preserve cpa value
        // If event_number_for_cpa starts with "events", it's a Conversion type
        if (hasConversionEvent) {
            // Ensure event_number_for_cpa is in the correct format
            const conversionNum = dealsFormData.event_number_for_cpa.replace("events", "");
            if (["1", "2", "3", "4", "5"].includes(conversionNum)) {
                dealsFormData.event_number_for_cpa = `events${conversionNum}`;
            }
            // Restore preserved CPA value if it was saved earlier
            if (preservedCpaValue !== null) {
                dealsFormData.cpa = preservedCpaValue;
            } else {
                // If no preserved value, check current value and convert if needed
                if (dealsFormData.cpa !== null && dealsFormData.cpa !== undefined && dealsFormData.cpa !== "") {
                    const cpaValue = typeof dealsFormData.cpa === "string" ? parseFloat(dealsFormData.cpa) : Number(dealsFormData.cpa);
                    if (!isNaN(cpaValue) && cpaValue > 0) {
                        dealsFormData.cpa = cpaValue;
                    } else {
                        dealsFormData.cpa = null;
                    }
                } else {
                    dealsFormData.cpa = null;
                }
            }
        }

        const budgetTypeBeforeDelete = scheduleFormData.budget_type;
        delete scheduleFormData.budget_type;
        delete scheduleFormData.daily_limits_type;

        // Wiping the lifetime figures is a CAMPAIGN-only rule: it exists so that switching the
        // campaign's budget_type radio to "daily" clears them. A line item has no such radio — its
        // budget_type is merely inferred from the merged parent schedule — so running the wipe here
        // would null the lifetime budget the user just typed. And because getData() feeds both sides
        // of the diff, it would do so on both, producing an empty, silent, no-op save.
        if (!this._isStrategyContext) {
            if (budgetTypeBeforeDelete === "daily") {
                scheduleFormData.max_global_cost = null;
                scheduleFormData.max_global_impressions = null;
                scheduleFormData.max_global_clicks = null;
            } else if (!scheduleFormData.max_global_cost || scheduleFormData.max_global_cost <= 0) {
                scheduleFormData.max_global_cost = null;
                scheduleFormData.max_global_impressions = null;
                scheduleFormData.max_global_clicks = null;
            }
        }

        // MediaSmart types every budget and frequency figure as `number`, and everything points to
        // it ignoring a quoted one in silence: it answers 200, stores nothing, and the key never
        // comes back in its echo — which is how a line item lost the 5.000 € a trader had just
        // typed (QA, 07/08/2026). Our inputs emit strings: text-input-form-field binds [type]
        // dynamically, and Angular's NumberValueAccessor only kicks in on the static type="number"
        // attribute, so the default (string-writing) accessor is what runs.
        //
        // Casting here rather than in that shared component keeps the blast radius to this save
        // path, and mirrors what campaign-create.component.ts already does with parseFloat when it
        // builds its own payload. It runs for BOTH contexts on purpose — the Budget section is the
        // same component for a campaign and for a line item, so both were sending strings.
        //
        // An empty input travels as null, never as "" or 0: null is MediaSmart's "auto" for a cap
        // and the only way the UI has of clearing one — the backend's merge guard preserves
        // explicit nulls precisely for that. 0 would read as "spend nothing".
        SCHEDULE_NUMERIC_FIELDS.forEach((field) => {
            const value = scheduleFormData[field];

            if (value === "" || value === undefined) {
                scheduleFormData[field] = null;
                return;
            }

            if (typeof value === "string") {
                const numericValue = parseFloat(value);
                // A non-numeric string is left as it is: rewriting it to 0 would hide a real bug.
                if (!isNaN(numericValue)) {
                    scheduleFormData[field] = numericValue;
                }
            }
        });

        if (scheduleFormData.started_at) {
            scheduleFormData.started_at = this.obtenerFechaDeTimeStamp(scheduleFormData.started_at);
        }

        if (scheduleFormData.finished_at) {
            scheduleFormData.finished_at = this.obtenerFechaDeTimeStamp(scheduleFormData.finished_at);
        }

        const payload = merge(
            {},
            generalFormData,
            { deals_and_pricing: dealsFormData },
            { targeting: targetingFormData },
            { retargeting: retargetingFormData },
            { schedule: scheduleFormData },
            { creatives: creativesFormData }
        );

        // Send advertiser as event_id field
        if (payload.advertiser) {
            payload.event_id = payload.advertiser;
            delete payload.advertiser;
        }

        if (payload.tracking_tool !== "image_pixel") {
            delete payload.bundle;
        }

        if ("selected_metrics" in payload) {
            delete payload.selected_metrics;
        }

        if (this._isStrategyContext) {
            for (const key of CAMPAIGN_PRICING_KEYS) {
                delete payload[key];
            }
            // Plan 31 · D12 (F21.7) — the rate card is the line's own cost and stays in the payload.
            payload.rate_card_cpm = toNumberOrNull(payload.rate_card_cpm);
        } else {
            // The campaign has no rate card: only a line buys at one.
            delete payload.rate_card_cpm;
            this._normalisePricingPayload(payload);
        }

        if (payload.strategy?.deal && Array.isArray(payload.strategy.deal)) {
            payload.strategy.deal = payload.strategy.deal.map((deal: any) => (deal?.key ? deal.key : deal)).filter(Boolean);
        }

        const hasExistingSyncCampaigns = Boolean((this.campaign as any)?.sync_campaigns?.length);
        const isSynchronizedCampaign = (this.campaign as any)?.type === "synchronized";

        if (generalFormData.sync_campaign_creation === true && !hasExistingSyncCampaigns && !isSynchronizedCampaign) {
            const syncPricing: any = {
                cpa: null,
                cpc: null,
                cpm: null,
                cpv: null,
                cpv_event: "",
                event_number_for_cpa: "",
                event_number_for_video_completion: "",
                max_cpm: null,
            };

            if (generalFormData.sync_strategy_pricing_type && generalFormData.sync_strategy_pricing_value) {
                if (generalFormData.sync_strategy_pricing_type === "videocomplete") {
                    syncPricing.cpv = parseFloat(generalFormData.sync_strategy_pricing_value);
                    syncPricing.cpv_event = "videocomplete";
                } else {
                    syncPricing[generalFormData.sync_strategy_pricing_type] = parseFloat(generalFormData.sync_strategy_pricing_value);
                }
            }

            payload.synchronization = {
                name: generalFormData.sync_name || "",
                max_daily_cost: generalFormData.sync_max_cost ? parseFloat(generalFormData.sync_max_cost) : null,
                deals_and_pricing: syncPricing,
            };
            payload.sync_campaign_creation = true;
        } else {
            delete payload.sync_campaign_creation;
        }

        return payload;
    }

    /**
     * Plan 31 · the resource sends `lifetime_goal_impressions` as {derived, override, effective}
     * and `cost_breakdown` as null when there is none; the form edits the override scalar and
     * needs explicit nulls (patchValue ignores a null group, which would leave stale fees behind).
     */
    private _mapPricingForForm(mapped: any): void {
        const lifetimeGoal = mapped.lifetime_goal_impressions;
        if (lifetimeGoal && typeof lifetimeGoal === "object") {
            mapped.lifetime_goal_impressions = lifetimeGoal.override ?? null;
        }

        if ("cost_breakdown" in mapped && !mapped.cost_breakdown) {
            mapped.cost_breakdown = {
                platform_fee_mode: null,
                platform_fee_kind: null,
                platform_fee_value: null,
                data_fee_kind: null,
                data_fee_value: null,
                agency_markup_pct: null,
            };
        } else if (mapped.cost_breakdown && mapped.cost_breakdown.platform_fee_value === undefined) {
            // Plan 31 · R7 — snapshots written before the tech fee kind carry the percentage.
            mapped.cost_breakdown = {
                ...mapped.cost_breakdown,
                platform_fee_kind: mapped.cost_breakdown.platform_fee_kind ?? "pct",
                platform_fee_value: mapped.cost_breakdown.platform_fee_pct ?? null,
            };
        }

        if (mapped.client_pricing && typeof mapped.client_pricing === "object") {
            mapped.client_pricing = { ...mapped.client_pricing, currency: mapped.client_pricing.currency ?? "" };
        }
    }

    /**
     * Plan 31 · shapes the campaign pricing groups the way StoreCampaignRequest validates them:
     * numbers (the inputs emit strings), null for blanks, `cost_breakdown: null` without a
     * platform fee mode, and a data fee that is either fully defined or absent.
     */
    private _normalisePricingPayload(payload: any): void {
        const breakdown = payload.cost_breakdown;
        if (!breakdown || !breakdown.platform_fee_mode) {
            payload.cost_breakdown = null;
        } else {
            const kind = breakdown.data_fee_kind || null;
            const value = toNumberOrNull(breakdown.data_fee_value);
            const halfDefined = (kind === null) !== (value === null);
            payload.cost_breakdown = {
                platform_fee_mode: breakdown.platform_fee_mode,
                // Plan 31 · R7 — the tech fee is a percentage unless it is explicitly a fixed CPM.
                platform_fee_kind: breakdown.platform_fee_kind || "pct",
                platform_fee_value: toNumberOrNull(breakdown.platform_fee_value),
                data_fee_kind: halfDefined ? null : kind,
                data_fee_value: halfDefined ? null : value,
                agency_markup_pct: toNumberOrNull(breakdown.agency_markup_pct),
            };
        }

        payload.gross_margin_enabled = payload.gross_margin_enabled === true;
        payload.gross_margin_pct = toNumberOrNull(payload.gross_margin_pct);
        payload.lifetime_goal_impressions = toNumberOrNull(payload.lifetime_goal_impressions);
        payload.lifetime_goal_units = toNumberOrNull(payload.lifetime_goal_units);
        if (payload.pricing_plan_id === "") {
            payload.pricing_plan_id = null;
        }
        if (payload.client_pricing && typeof payload.client_pricing === "object" && !payload.client_pricing.currency) {
            delete payload.client_pricing.currency;
        }
    }

    obtenerFechaDeTimeStamp(date: Date) {
        const today = new Date(date);
        const year = today.getFullYear();
        let month = (today.getMonth() + 1).toString().padStart(2, "0");
        let day = today.getDate().toString().padStart(2, "0");

        return `${year}-${month}-${day}`;
    }

    public markAllAsTouched(): void {
        this.markFormGroupTouched(this.form);
        this.markFormGroupTouched(this.scheduleForm);
        this.markFormGroupTouched(this.targetingForm);
        this.markFormGroupTouched(this.dealsForm);
        this.markFormGroupTouched(this.creativesForm);
        this.markFormGroupTouched(this.retargeting);
    }

    public resetAllForm(): void {
        // _generateAllForms() rebuilds the FormGroups from scratch, so the declarative (campaign)
        // budget validators are already back in place — only the context flag has to be cleared.
        this._isStrategyContext = false;
        this._generateAllForms();
        this._checkTabErrors();
        this._cleanAllErrors();
    }

    setFormError(tabIndex: number, hasError: boolean): void {
        this._campaignErrorService.setError(tabIndex, hasError);
    }

    public setOriginalForm() {
        this.originalForm = this.getStringForm();
        this.setOriginalFormData();
        this.markAllFormsPristine();
    }

    public formHasChanged() {
        const original = this.originalForm;
        const current = this.getStringForm();

        return original !== current;
    }

    public setOriginalFormData() {
        try {
            const data = this.getData();
            this.originalFormData = JSON.parse(JSON.stringify(data));
        } catch (error) {
            console.error("[setOriginalFormData] Error saving original data:", error);
            this.originalFormData = null;
        }
    }

    /**
     * Top-level targetingForm keys that differ from the last known-saved baseline.
     * Used to stop a background campaign/strategy refresh (tab switch, onTabChange)
     * from silently overwriting in-progress edits before the user hits Save — see
     * PLAN_PUBLISHER_LISTS_REDESIGN.md for the reported symptom this fixes.
     *
     * Deliberately per-key (not a single all-or-nothing boolean): an unsaved edit to
     * one facet (e.g. `publishers`) must not block a fresh server value from reaching
     * an untouched facet (e.g. `countries`) on the same refresh.
     */
    public getUnsavedTargetingKeys(): string[] {
        if (!this.originalFormData) {
            return [];
        }
        // originalFormData.targeting is getData()'s post-transform shape (arrays -> {value:true}
        // maps, some keys conditionally deleted); normalize the raw form value the same way
        // before diffing, or every one of those keys reads as permanently "changed".
        const currentTargeting = this._normalizeTargetingShape({ ...this.targetingForm.getRawValue() });
        const diff = this.getDeepDiff(this.originalFormData.targeting || {}, currentTargeting);
        return diff ? Object.keys(diff) : [];
    }

    public getChangedDataDirect(): any {
        if (!this.originalFormData) {
            return this.getData();
        }

        const currentData = this.getData();
        const diff = this.getDeepDiff(this.originalFormData, currentData);

        if (!diff || Object.keys(diff).length === 0) {
            return {};
        }

        return diff;
    }

    private _checkTabErrors(isStrategy?: boolean) {
        if (isStrategy === true) {
            this.setFormError(0, !this.form.valid);
            this._logFormErrors(this.form, "General Form");

            this.setFormError(1, !this.targetingForm.valid);
            this._logFormErrors(this.targetingForm, "Targeting Form");

            this.setFormError(2, !this.scheduleForm.valid);
            this._logFormErrors(this.scheduleForm, "Schedule Form");

            this.setFormError(3, !this.dealsForm.valid);
            this._logFormErrors(this.dealsForm, "Deals Form");

            this.setFormError(4, !this.creativesForm.valid);
            this._logFormErrors(this.creativesForm, "Creatives Form");

            this.setFormError(5, !this.retargeting.valid);
            this._logFormErrors(this.retargeting, "Retargeting Form");
        } else {
            this.setFormError(1, !this.form.valid);
            this._logFormErrors(this.form, "General Form");

            this.setFormError(2, !this.targetingForm.valid);
            this._logFormErrors(this.targetingForm, "Targeting Form");

            this.setFormError(3, !this.scheduleForm.valid);
            this._logFormErrors(this.scheduleForm, "Schedule Form");

            this.setFormError(4, !this.dealsForm.valid);
            this._logFormErrors(this.dealsForm, "Deals Form");

            this.setFormError(5, !this.creativesForm.valid);
            this._logFormErrors(this.creativesForm, "Creatives Form");

            this.setFormError(7, !this.retargeting.valid);
            this._logFormErrors(this.retargeting, "Retargeting Form");
        }
    }

    private _logFormErrors(formGroup: FormGroup, formName: string) {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this._logFormErrors(control, `${formName} - ${key}`);
            } else if (control && control.invalid) {
                const errors = JSON.stringify(control.errors);
                console.warn(`${formName} - Campo "${key}" tiene errores: ${errors}`);
            }
        });
    }

    private _cleanAllErrors() {
        this.setFormError(1, false);
        this.setFormError(2, false);
        this.setFormError(3, false);
        this.setFormError(4, false);
        this.setFormError(5, false);
        this.setFormError(6, false);
    }

    /**
     * Clear the error badge on every tab index, in both the campaign and the
     * strategy layouts. _checkTabErrors uses different indices depending on
     * isStrategy (strategy mode sets index 0 for the general form, campaign
     * mode uses index 1), so a badge raised while validating a line item in
     * the edit modal (strategy mode) would otherwise linger on the campaign
     * view's Properties tab (index 0). Used when tearing down the line item
     * edit modal to leave the surrounding campaign view clean.
     */
    public clearAllTabErrors(): void {
        // Upper bound mirrors the highest tab index _checkTabErrors can raise
        // (campaign mode sets up to index 7). Keep both in sync if that changes.
        const maxTabErrorIndex = 7;
        for (let i = 0; i <= maxTabErrorIndex; i++) {
            this.setFormError(i, false);
        }
    }

    /**
     * Generate all forms.
     *
     * @private
     */
    private _generateAllForms() {
        this.form = this._formBuilder.group({
            id: [""],
            name: ["", [Validators.required, Validators.minLength(8)]],
            type: [""],
            campaign_type: [""],
            color: ["rgb(255,50,75)"],
            description: [""],
            goal: this._formBuilder.group({
                kpi: ["cpm"],
                value: ["", [(control: AbstractControl) => this._goalCpmMinimum(control)]],
            }),
            advertiser: [""],
            advertiser_name: [""],
            trader_ids: [[]],
            advertiser_domain: ["", [FormValidator.domain()]],
            client_pricing: this._formBuilder.group({
                model: [""],
                value: [""],
                currency: [""],
            }),
            // Plan 31 · F8.3 — campaign-level pricing (B4: no line item override).
            pricing_plan_id: [null],
            cost_breakdown: this._formBuilder.group({
                platform_fee_mode: [null],
                // Plan 31 · R7 — `pct` (a share, < 100) or `fixed_cpm` (a price per mille).
                platform_fee_kind: ["pct"],
                platform_fee_value: [null, [Validators.min(0)]],
                data_fee_kind: [null],
                data_fee_value: [null, [Validators.min(0)]],
                agency_markup_pct: [null, [Validators.min(0), Validators.max(99.99)]],
            }),
            gross_margin_enabled: [false],
            gross_margin_pct: [null, [Validators.min(0), Validators.max(99.99)]],
            lifetime_goal_impressions: [null, [Validators.min(1)]],
            lifetime_goal_units: [null, [Validators.min(1)]],
            iab_categories: [[]],
            state: ["inactive"],
            tags: [[]],
            cross_device: [""],
            opportunity_window: [""],
            sync_campaign_creation: [false],
            sync_name: [""],
            sync_max_cost: [null],
            sync_strategy_pricing_type: ["cpm"],
            sync_strategy_pricing_value: [null],
            radius_around_dooh: [""],
            created_at: [""],
            updated_at: [""],
            tracking_tool: ["none"],
            accept_googleadx_fee: [true],
            ask_viewability_tracking_tool: [false],
            viewability_tracking_tool: [""],
            measure_viewability: [false],
            viewability_terms_and_conditions: [false, [FormValidator.requiredIfValue("measure_viewability", true)]],
            bundle: ["", [FormValidator.onlyNumbers()]],
            attribution_window_on_click: ["", Validators.max(120)],
            attribution_window_on_impression: [""],
            conversion_one_is_required: [false],
            supports_many_conversions_one: [false],
            supports_many_conversions_other: [false],
            conversion_names: this._formBuilder.group({
                events2: [""],
                events3: [""],
                events4: [""],
                events5: [""],
            }),
            terms_ramps_id: [false],
            empowered_by_liveramp: [false],
            creatives: [[]],
            extend_state: [false],
            cost_percentage_cross_device: [""],
            cpc_cross_device: [""],
            cpm_cross_device: [""],
            event_id: [""],
            labels: [[]],
            cost_percentage: [""],
            // Plan 31 · D12/F21.7 — lo que la línea Perion compra el inventario: es coste nuestro,
            // así que solo lo ve y lo escribe quien gestiona pricing. El backend lo descarta del resto.
            rate_card_cpm: [null, [Validators.min(0)]],
            count_reengagement_events: [false],
            use_custom_conversion_names: [null],
            isVideoCreative: [false],
            empowered_by_liveramp_tos: [false, [FormValidator.requiredIfValue("empowered_by_liveramp", true)]],
            selected_metrics: [[]],
            parent: [""],
            // Perion DOOH only — campaign-level metadata edited from the
            // 'Campaign Info' tab. Declared here so `_fillForm` patches the
            // values straight from the campaign payload.
            perion_campaign_frequency_cap_seconds: [0],
            perion_note: [""],
        });

        // The minimum depends on the KPI, so a KPI change has to re-evaluate the value. Subscribed
        // here, on the form just built: resetAllForm() replaces this.form, and a subscription made
        // once in the constructor would stay on the discarded one.
        const goalForm = this.form;
        goalForm
            .get("goal.kpi")
            ?.valueChanges.subscribe(() => goalForm.get("goal.value")?.updateValueAndValidity({ emitEvent: false }));

        this.dealsForm = this._formBuilder.group({
            deal_policy: [DealsPolicies.OFF],
            deals: [[]],
            cpc: ["", [Validators.min(this._bidMinimums.getCpcMinimum())]],
            cpm: ["", [Validators.max(200), Validators.min(this._bidMinimums.getCpmMinimum())]],
            max_cpm: [null, [Validators.min(this._bidMinimums.getCpmMinimum())]],
            exchange_breakdown: [true],
            optimization_type: [OptimizationTypes.OFF],
            cpa: ["0", [Validators.min(0)]],
            event_number_for_cpa: [""],
            event_number_for_video_completion: [""],
            cpv: ["", [Validators.min(0)]],
            cpv_event: [""],
            rules: [[]],
            label_engagement_optimization: [""],
            video_optimization_available: [true],
        });

        this.targetingForm = this._formBuilder.group({
            weather_contexts: [[]],
            categories_vendor: ["iab"],
            categories: [[]],
            publishers: [[]],
            blacklist_publishers: [[]],
            use_publishers_as_blacklist: [false],
            ignore_global_blacklists: [false],
            publishers_rules: [[]],
            exchanges: [""],
            use_categories_as_blacklist: [false],
            use_weather_contexts_as_blacklist: [false],
            device_type: [[]],
            connection: [[]],
            use_connection_as_blacklist: [false],
            device_models: [[]],
            use_device_models_as_blacklist: [false],
            device_makers: [[]],
            use_device_makers_as_blacklist: [false],
            isps: [[]],
            use_isps_as_blacklist: [false],
            browsers: [[]],
            use_browsers_as_blacklist: [false],
            os: [null],
            publisher_type: [{ app: true, web: true }],
            user_agents: [[]],
            use_user_agents_as_blacklist: [false],
            dmp_audiences: [[]],
            app_rating: [
                {
                    missing: true,
                    rating1: true,
                    rating2: true,
                    rating3: true,
                    rating4: true,
                    rating5: true,
                },
            ],
            min_app_rating: [null],
            mandatory_ads_txt: [false],
            adstxt_publisher_list: [[], [FormValidator.requiredArrayIfValue("mandatory_ads_txt", true)]],
            adstxt_publishers: [""],
            mandatory_brand_safety: [false],
            mandatory_bundle: [false],
            mandatory_tagid: [false],
            mandatory_domain: [false],
            mandatory_ip_address: [false],
            remove_safeframe: [false],
            measurement: [""],
            is_auction_type: [false],
            auction_type: [""],
            identifiers: [[]],
            is_universal_ids: [false],
            universal_ids: [""],
            age: [""],
            gender: [""],
            languages: [[]],
            use_languages_as_blacklist: [false],
            use_age_as_blocklist: [false],
            use_gender_as_blocklist: [false],
            exclude_dnt: [false],
            is_sync_with_dooh_campaign: [false],
            dooh_campaign: [""],
            audiences_rules: ["audiences"],
            // Sin validador: MediaSmart rechaza Mandatory+lookalikes a nivel de campaña
            // (lo permite en line items), así que exigir dmp_audiences_and con lookalikes
            // era incorrecto. El aviso vive en audiences-component (no bloquea el guardado).
            dmp_audiences_and: [[]],
            dmp_audiences_or: [[]],
            dmp_audiences_not: [[]],
            dmp_audiences_blacklist: [false],
            will_not_copy_audiences: [false],
            contexts: [[]],
            contexts_not: [[]],
            manual_keywords_and: [[]],
            manual_keywords_or: [[]],
            manual_keywords_not: [[]],
            countries: [[], [Validators.required]],
            regions: [[]],
            use_regions_as_blacklist: [false],
            cities: [[]],
            zipcodes: [[]],
            audience_mapping_value: [""],
            targeting_geolist: [""],
            geolist_acquisition: [""],
            geolist_conversion: [""],
            conversion_geolist: [""],
            audienceSelector: [""],
            video_positions: [null],
            rewarded: [false],
            is_age: [false],
            is_gender: [false],
            use_geolist_conversion_as_targeting_blacklist: [false],
            distribute_among_areas: [false],
            use_targeting_users_list_for_pretargeting: [false],
            audience_mapping_inherit: [false],
            operators: [[]],
            use_operators_as_blacklist: [false],
            seconds_on_screen: [1],
            percentage_of_pixels: [50],
            traffic_viewability: [""],
            custom_viewability: [false],
            only_green_traffic: [false],
        });

        // Revalidate Ads.txt publisher type when toggle changes (avoid subscriptions inside validators)
        this.targetingForm.get("mandatory_ads_txt")?.valueChanges.subscribe(() => {
            this.targetingForm.get("adstxt_publisher_list")?.updateValueAndValidity({ onlySelf: true });
        });

        this.retargeting = this._formBuilder.group({
            radius: [""],
            use_targeting_users_list_as_blacklist: [false],
            users_list_on_adviewed: [""],
            users_list_on_clicks: [""],
            users_list_on_impressions: [""],
            users_lists_on_events: this._formBuilder.group({
                on_event_1: [""],
                on_event_2: [""],
                on_event_3: [""],
                on_event_4: [""],
                on_event_5: [""],
            }),
            users_lists_on_video: this._formBuilder.group({
                on_video_complete: [""],
                on_video_first_quartile: [""],
                on_video_second_quartile: [""],
                on_video_start: [""],
                on_video_third_quartile: [""],
            }),
        });

        this.scheduleForm = this._formBuilder.group({
            add_other_costs_to_limits: [false],
            budget_limit_publisher: [""],
            budget_type: ["lifetime"],
            delivered_cost: [0],
            daily_limits_type: ["auto"],
            finished_at: ["", Validators.required],
            started_at: ["", Validators.required],
            max_daily_clicks: ["", FormValidator.noDecimals()],
            max_daily_cost: ["", [Validators.min(10)]],
            max_daily_impressions: ["", [Validators.min(1000), FormValidator.noDecimals()]],
            max_global_clicks: ["", [FormValidator.noDecimals()]],
            max_global_cost: ["", [Validators.min(10)]],
            max_global_impressions: ["", [Validators.min(1000), FormValidator.noDecimals()]],
            max_impressions_user: [""],
            max_impressions_user_day: [""],
            max_impressions_user_hour: [""],
            max_adviewed_user: [""],
            max_adviewed_user_day: [""],
            max_adviewed_user_hour: [""],
            one_click_per_user: [false],
            timezone: [""],
            timing: [[]],
            spent_distribution: ["uniform"],
            uniform_distribution: [false],
            max_user_awareness: [false],
        });

        this.scheduleForm.get("spent_distribution").valueChanges.subscribe((value) => {
            this.scheduleForm.get("uniform_distribution").setValue(value === "uniform");
        });

        // NOTE: The "default values" section (distribution/click_url/impression_urls/attributes)
        // is commented out in creativities.component.html, so these controls have no editable UI.
        // They are only fed by patchValue(campaign.creatives) on load, so URL validators here would
        // block saving on backend/stale values the user cannot fix. Keep the controls, drop validators.
        this.creativesForm = this._formBuilder.group({
            distribution: ["none"],
            campaign_creatives: [[]],
            attributes: [[]],
            click_url: [""],
            impression_urls: [[]],
            default_attributes: [[]],
            default_click_url: [""],
            default_impression_urls: [[]],
        });

        this.wizardForm = this._formBuilder.group({
            campaign_type: ["generic"],
            advertiser: [""],
            state: ["active"],
            name: ["", [Validators.required, Validators.minLength(8)]],
            color: ["rgb(255,50,75)", [Validators.required]],
            ask_creatives: [false],
            advertiser_domain: ["", [Validators.required, FormValidator.domain()]],
            iab_categories: [[], [Validators.required]],
            countries: [[]],
            ask_schedule: [true],
            started_at: ["", Validators.required],
            finished_at: ["", Validators.required],
            max_global_cost: ["", [Validators.required, Validators.min(10)]],
            max_global_clicks: [
                "",
                [FormValidator.requiredIfAnyControlHasNoValue("max_global_clicks", "max_global_impressions"), FormValidator.noDecimals()],
            ],
            max_global_impressions: [
                "",
                [
                    FormValidator.requiredIfAnyControlHasNoValue("max_global_impressions", "max_global_clicks"),
                    Validators.min(1000),
                    FormValidator.noDecimals(),
                ],
            ],
            ask_viewability_tracking_tool: [false],
            viewability_tracking_tool: [[]],
            ask_type_of_tracking: ["app"],
            tracking_tool: ["none"],
            ask_private_deals: [false],
            include_video: [false],
            cpm: ["", [Validators.max(200), Validators.min(this._bidMinimums.getCpmMinimum())]],
            cpc: ["", [Validators.min(this._bidMinimums.getCpcMinimum())]],
            cpv: [""],
            cpv_event: [""],
            kpi: ["", [Validators.required]],
            value: ["", [Validators.required]],
            creatives: [[]],
            cross_device: [false],
            opportunity_window: [[]],
            max_daily_clicks: [""],
            max_daily_cost: ["10"],
            max_daily_impressions: [""],
            cost_percentage_cross_device: ["", FormValidator.requiredIfValue("cross_device", true)],
            measure_viewability: [false],
            cpm_cross_device: [""],
            cpc_cross_device: [""],
            addtrackingTool: [false],
            os: [null],
            deals: [[]],
            geolist_acquisition: [""],
            geolist_conversion: [[]],
            ask_geolist_conversion: [false],
            ask_footfall: [false],
            type_of_creative: ["image"],
            geo_list_id: [""],
            follow_up_method: [""],
            time_of_report: [""],
            geo_list: [""],
            areas: [[]],
            areas_data: [[]],
            areas_data_dirty: [[]],
            ooh_id: [""], // ID de la campaña OOH importada
            patrimonies: [[]], // IDs de los patrimonios seleccionados
            maximize_video: [""],
            targeting: this._formBuilder.group({
                geolist_acquisition: [""],
            }),
            deal_policy: [""],
        });

        this.audienceForm = this._formBuilder.group({
            name: ["", Validators.required, Validators.min(8)],
            description: [""],
            file: [null],
        });

        this.duplicateForm = this._formBuilder.group({
            name: ["", [Validators.required, Validators.minLength(8)]],
        });
        this.duplicateStrategyForm = this._formBuilder.group({
            name: ["", [Validators.required, Validators.minLength(8)]],
        });
        this.contactUs = this._formBuilder.group({
            name: ["", Validators.required],
            email: ["", Validators.required],
            subject: ["", Validators.required],
            description: [""],
            file: [null],
            accept_tos: [false, Validators.required],
        });

        this.setOriginalForm();

        this._campaignService.options$.subscribe((data) => (this.options = data));
    }

    private _fillForm() {
        const unsavedTargetingKeys = this.getUnsavedTargetingKeys();
        const previousTargetingBaseline = unsavedTargetingKeys.length ? { ...(this.originalFormData?.targeting || {}) } : null;

        if (this.campaign) {
            const mappedCampaign = { ...this.campaign };
            const c = this.campaign as any;
            mappedCampaign.trader_ids = getTraderIdsFromCampaign(c);

            if (this.campaign.advertiser && typeof this.campaign.advertiser === "object") {
                mappedCampaign.advertiser = this.campaign.advertiser.event_id || this.campaign.advertiser.id || "";
                if (this.campaign.advertiser.name) {
                    mappedCampaign.advertiser_name = this.campaign.advertiser.name;
                }
                if (this.campaign.advertiser.domain) {
                    mappedCampaign.advertiser_domain = this.campaign.advertiser.domain;
                }
                if (this.campaign.advertiser.iab_category) {
                    if (!this.campaign.iab_categories || this.campaign.iab_categories.length === 0) {
                        mappedCampaign.iab_categories = [this.campaign.advertiser.iab_category];
                    }
                }
            }

            this._mapPricingForForm(mappedCampaign);
            this.form.patchValue(mappedCampaign);
            this.form.get("trader_ids")?.setValue(mappedCampaign.trader_ids ?? [], { emitEvent: true });

            if (!this._isStrategyContext) {
                // Lo vuelve a fijar el schedule de abajo; sin schedule, no hay reparto que enseñar.
                this.campaignAutoDailyShare = null;
            }

            if (this.campaign.schedule) {
                const scheduleData = { ...this.campaign.schedule };

                if (scheduleData.started_at && typeof scheduleData.started_at === "string") {
                    scheduleData.started_at = new Date(scheduleData.started_at);
                }
                if (scheduleData.finished_at && typeof scheduleData.finished_at === "string") {
                    scheduleData.finished_at = new Date(scheduleData.finished_at);
                }

                scheduleData.budget_type = this.determineBudgetType(scheduleData);

                // Solo una campaña: el schedule de un line item llega mezclado con el de su campaña
                // (mergeWithParent) y sus diarios son suyos.
                this.scheduleForm.patchValue(
                    this._isStrategyContext ? scheduleData : this.takeCampaignAutoDailyShare(scheduleData, this.campaign.dsp_provider)
                );
            }
            if (this.campaign.targeting) {
                const currentTargetingValue = this.targetingForm.getRawValue();
                const mergedTargeting: any = { ...this.campaign.targeting };
                unsavedTargetingKeys.forEach((key) => {
                    mergedTargeting[key] = currentTargetingValue[key];
                });

                this.targetingForm.patchValue(mergedTargeting);
                this._normalizeAdsTxtPublisherListControl();
                const targeting = this.campaign.targeting as any;
                const seconds = targeting.seconds_on_screen;
                const pixels = targeting.percentage_of_pixels;
                if ((seconds != null && Number(seconds) > 1) || (pixels != null && Number(pixels) > 50)) {
                    this.targetingForm.get("custom_viewability")?.setValue(true);
                }
            }
            if (this.form.get("measure_viewability")?.value === true) {
                this.form.get("viewability_terms_and_conditions")?.setValue(true);
            }
            if (this.campaign.deals_and_pricing) {
                this.dealsForm.patchValue(this.campaign.deals_and_pricing);
            }
            if (this.campaign.creatives) {
                this.creativesForm.patchValue(this.campaign.creatives);
            }
            if (this.campaign.retargeting) {
                this.retargeting.patchValue(this.campaign.retargeting);
            }
        }
        this._applyBudgetValidatorsForContext();
        this.setOriginalForm();
        // setOriginalForm() just re-baselined everything against the CURRENT form,
        // including the targeting keys we deliberately preserved above — restore just
        // those keys' pre-edit baseline so getChangedData() still picks them up on the
        // next Save. Untouched keys (e.g. countries) got the fresh server baseline, as normal.
        if (unsavedTargetingKeys.length && this.originalFormData && previousTargetingBaseline) {
            unsavedTargetingKeys.forEach((key) => {
                this.originalFormData.targeting[key] = previousTargetingBaseline[key];
            });
        }
    }
    private _fillStrategyForm() {
        const unsavedTargetingKeys = this.getUnsavedTargetingKeys();
        const previousTargetingBaseline = unsavedTargetingKeys.length ? { ...(this.originalFormData?.targeting || {}) } : null;

        if (this.campaign) {
            this.form.patchValue(this.campaign);
            this.scheduleForm.patchValue(this.campaign.schedule);

            // For strategies, don't automatically fill deals_and_pricing here
            // It will be handled specifically by updateDealsFormForStrategy()
            // to preserve strategy-specific values including empty strings

            this.creativesForm.patchValue(this.campaign.creatives);
            this.retargeting.patchValue(this.campaign.retargeting);
            const mappedCampaign = { ...this.campaign };
            const cStrategy = this.campaign as any;
            mappedCampaign.trader_ids = getTraderIdsFromCampaign(cStrategy);

            if (this.campaign.advertiser && typeof this.campaign.advertiser === "object") {
                mappedCampaign.advertiser = this.campaign.advertiser.event_id || this.campaign.advertiser.id || "";
                if (this.campaign.advertiser.name) {
                    mappedCampaign.advertiser_name = this.campaign.advertiser.name;
                }
                if (this.campaign.advertiser.domain) {
                    mappedCampaign.advertiser_domain = this.campaign.advertiser.domain;
                }
                if (this.campaign.advertiser.iab_category) {
                    if (!this.campaign.iab_categories || this.campaign.iab_categories.length === 0) {
                        mappedCampaign.iab_categories = [this.campaign.advertiser.iab_category];
                    }
                }
            }

            this.form.patchValue(mappedCampaign);
            this.form.get("trader_ids")?.setValue(mappedCampaign.trader_ids ?? [], { emitEvent: true });

            if (this.campaign.schedule) {
                const scheduleData = { ...this.campaign.schedule };

                if (scheduleData.started_at && typeof scheduleData.started_at === "string") {
                    scheduleData.started_at = new Date(scheduleData.started_at);
                }
                if (scheduleData.finished_at && typeof scheduleData.finished_at === "string") {
                    scheduleData.finished_at = new Date(scheduleData.finished_at);
                }

                scheduleData.budget_type = this.determineBudgetType(scheduleData);

                this.scheduleForm.patchValue(scheduleData);
            }

            if (this.campaign.creatives) {
                this.creativesForm.patchValue(this.campaign.creatives);
            }
            if (this.campaign.retargeting) {
                this.retargeting.patchValue(this.campaign.retargeting);
            }
        }
        this._fillStrategy(this.campaign, unsavedTargetingKeys);
        this._applyBudgetValidatorsForContext();
        this.setOriginalForm();
        if (unsavedTargetingKeys.length && this.originalFormData && previousTargetingBaseline) {
            unsavedTargetingKeys.forEach((key) => {
                this.originalFormData.targeting[key] = previousTargetingBaseline[key];
            });
        }
    }

    private _fillStrategy(strategy: any, unsavedTargetingKeys: string[] = []): void {
        const defaultValues = this.targetingForm.getRawValue();
        const updatedValues: any = {
            weather_contexts: strategy?.targeting.weather_contexts ?? defaultValues.weather_contexts,
            categories: strategy?.targeting.categories ?? defaultValues.categories,
            publishers: strategy?.targeting.publishers ?? defaultValues.publishers,
            blacklist_publishers: strategy?.targeting.blacklist_publishers ?? defaultValues.blacklist_publishers,
            use_publishers_as_blacklist: strategy?.targeting.use_publishers_as_blacklist ?? defaultValues.use_publishers_as_blacklist,
            ignore_global_blacklists: strategy?.targeting.ignore_global_blacklists ?? defaultValues.ignore_global_blacklists,
            publishers_rules: strategy?.targeting.publishers_rules ?? defaultValues.publishers_rules,
            exchanges: strategy?.targeting?.exchanges !== undefined ? strategy.targeting.exchanges : defaultValues.exchanges,
            device_type: strategy?.targeting.device_type ?? defaultValues.device_type,
            connection: strategy?.targeting.connection ?? defaultValues.connection,
            device_models: strategy?.targeting.device_models ?? defaultValues.device_models,
            use_device_models_as_blacklist:
                strategy?.targeting.use_device_models_as_blacklist ?? defaultValues.use_device_models_as_blacklist,
            device_makers: strategy?.targeting.device_makers ?? defaultValues.device_makers,
            use_device_makers_as_blacklist:
                strategy?.targeting.use_device_makers_as_blacklist ?? defaultValues.use_device_makers_as_blacklist,
            isps: strategy?.targeting.isps ?? defaultValues.isps,
            use_isps_as_blacklist: strategy?.targeting.use_isps_as_blacklist ?? defaultValues.use_isps_as_blacklist,
            sim_countries: strategy?.targeting.sim_countries ?? defaultValues.sim_countries,
            os: strategy?.targeting.os ?? defaultValues.os,
            publisher_type: strategy?.targeting.publisher_type ?? defaultValues.publisher_type,
            app_rating: strategy?.targeting.app_rating ?? defaultValues.app_rating,
            mandatory_ads_txt: strategy?.targeting.mandatory_ads_txt ?? defaultValues.mandatory_ads_txt,
            age: strategy?.targeting.age ?? defaultValues.age,
            gender: strategy?.targeting.gender ?? defaultValues.gender,
            languages: strategy?.targeting.languages ?? defaultValues.languages,
            exclude_dnt: strategy?.targeting.exclude_dnt ?? defaultValues.exclude_dnt,
            countries: strategy?.targeting.countries ?? defaultValues.countries,
        };

        // Keep the user's in-progress edit for any key that's currently unsaved — this
        // reads from `defaultValues` (captured above, before this patch) rather than
        // strategy?.targeting, so untouched keys still refresh from the fresh strategy data.
        unsavedTargetingKeys.forEach((key) => {
            if (key in updatedValues) {
                updatedValues[key] = defaultValues[key];
            }
        });

        this.targetingForm.patchValue(updatedValues);
        this._normalizeAdsTxtPublisherListControl();
    }

    private _normalizeAdsTxtPublisherListControl(): void {
        const ctrl = this.targetingForm.get("adstxt_publisher_list");
        if (!ctrl) return;

        const normalized = this._normalizeAdsTxtPublisherListValue(ctrl.value);
        // Avoid infinite loops: only set when it changes
        const current = Array.isArray(ctrl.value) ? ctrl.value.map(String) : ctrl.value;
        if (JSON.stringify(current) !== JSON.stringify(normalized)) {
            ctrl.setValue(normalized, { emitEvent: false });
        }
    }

    private _normalizeAdsTxtPublisherListValue(value: unknown): string[] {
        const rawList: string[] = (() => {
            if (value === null || value === undefined) return [];
            if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
            if (typeof value === "string") return value.trim() ? [value.trim()] : [];
            return [];
        })();

        // Backend sometimes returns enum-like values in uppercase
        const normalized = rawList
            .map((v) => v.trim())
            .filter(Boolean)
            .map((v) => v.toLowerCase())
            .map((v) => {
                if (v === "direct") return "direct";
                if (v === "reseller") return "resellers";
                if (v === "resellers") return "resellers";
                if (v === "verified") return "verified";
                return v;
            });

        // Deduplicate while preserving order
        const seen = new Set<string>();
        return normalized.filter((v) => {
            if (seen.has(v)) return false;
            seen.add(v);
            return true;
        });
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
            }
        });
    }

    private markAllFormsPristine(): void {
        [
            this.form,
            this.dealsForm,
            this.targetingForm,
            this.retargeting,
            this.scheduleForm,
            this.creativesForm,
        ].forEach((form) => this.markControlPristine(form));
    }

    private markControlPristine(control: AbstractControl | null): void {
        if (!control) {
            return;
        }

        control.markAsPristine({ onlySelf: true });
        control.markAsUntouched({ onlySelf: true });

        if (control instanceof FormGroup) {
            Object.values(control.controls).forEach((child) => this.markControlPristine(child));
        }
    }

    /**
     * Re-apply the budget validators that match the current context.
     *
     * Two reasons this lives here instead of in BudgetComponent:
     *
     * 1. The scheduleForm outlives the component. Switching tabs or closing the line item modal
     *    destroys BudgetComponent while the form stays in use, so validators applied from its
     *    ngOnInit used to leak across contexts — that leak is exactly what the manual
     *    error-stripping hotfixes in the save handlers were papering over.
     * 2. Both _fill methods run right before setOriginalForm(), which is the one moment where we
     *    reliably know whether the form now holds a campaign or a line item.
     *
     * Line item: no budget field is mandatory. Without a lifetime the line item runs in auto — no
     * budget of its own, it shares the campaign's and MediaSmart spends it on the line items that
     * perform best (product decision of 14/10/2026) — and clearing the lifetime of one that had it is
     * how a trader hands it back to auto; the API releases the cap it had derived. The lifetime used
     * to be required except on line items that predated it (PR #182), which blocked exactly that.
     * A Perion line item is the exception: it keeps its total required once it has one.
     * Campaign: restore the declarative validators from _generateAllForms and let BudgetComponent's
     * budget_type subscription own the lifetime/daily switch, as before.
     */
    private _applyBudgetValidatorsForContext(): void {
        const maxGlobalCost = this.scheduleForm.get("max_global_cost");
        const maxGlobalImpressions = this.scheduleForm.get("max_global_impressions");
        const maxGlobalClicks = this.scheduleForm.get("max_global_clicks");
        const maxDailyCost = this.scheduleForm.get("max_daily_cost");

        // Perion has no auto to fall back on: Hivestack requires a total per line item, and clearing
        // one here would send `budget: null` (TransformPerionUpdatePayloadAction's MS-shape fallback).
        // Its line items are edited in PerionLineItemsComponent; this covers one opened in the shared
        // form by URL, with the rule it had before — required once stored. The form still holds the
        // stored value at this point (see above), which is what tells "stored" apart.
        const storedLifetime = Number(maxGlobalCost?.value);
        const perionLineItemWithTotal =
            this._isStrategyContext &&
            this._campaign?.dsp_provider === "perion" &&
            Number.isFinite(storedLifetime) &&
            storedLifetime > 0;

        maxGlobalCost?.setValidators(
            perionLineItemWithTotal ? [Validators.required, Validators.min(10)] : [Validators.min(10)]
        );
        maxGlobalImpressions?.setValidators([Validators.min(1000), FormValidator.noDecimals()]);
        maxGlobalClicks?.setValidators([FormValidator.noDecimals()]);
        // A line item's daily caps carry no GeoActivate minimum (the DSP reports what it refuses);
        // the campaign keeps its own.
        const maxDailyImpressions = this.scheduleForm.get("max_daily_impressions");
        maxDailyCost?.setValidators(this._isStrategyContext ? [] : [Validators.min(10)]);
        maxDailyImpressions?.setValidators(
            this._isStrategyContext ? [FormValidator.noDecimals()] : [Validators.min(1000), FormValidator.noDecimals()]
        );

        [maxGlobalCost, maxGlobalImpressions, maxGlobalClicks, maxDailyCost, maxDailyImpressions].forEach((control) =>
            control?.updateValueAndValidity({ emitEvent: false })
        );
    }

    private determineBudgetType(scheduleData: any): "lifetime" | "daily" {
        const toPositiveNumber = (value: unknown): number => {
            if (value === null || value === undefined || value === "") {
                return 0;
            }

            const numericValue = Number(value);
            return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
        };

        const hasGlobalCost = toPositiveNumber(scheduleData?.max_global_cost) > 0;
        if (hasGlobalCost) {
            return "lifetime";
        }

        const hasDailyCost = toPositiveNumber(scheduleData?.max_daily_cost) > 0;
        const hasDailyImpressions = toPositiveNumber(scheduleData?.max_daily_impressions) > 0;
        const hasDailyClicks = toPositiveNumber(scheduleData?.max_daily_clicks) > 0;

        if (hasDailyCost || hasDailyImpressions || hasDailyClicks) {
            return "daily";
        }

        return "lifetime";
    }

    private getStringForm() {
        let stringForm = JSON.stringify(this.getData());
        stringForm = stringForm.replace(/true/g, "1");
        stringForm = stringForm.replace(/false/g, "0");
        stringForm = stringForm.replace(/null/g, '""');
        return stringForm;
    }

    /**
     * Get only changed fields (PATCH-like updates)
     */
    public getChangedData(): any {
        const changed = this._computeChangedData();
        completeVideoPositions(changed?.targeting, this.targetingForm.get("video_positions")?.value);

        // The API replaces `retargeting` as a whole (local column and DSP), so a partial diff such as
        // { users_lists_on_events: { on_event_3 } } would wipe every other Audience Builder block.
        // Campaign only: a line item form is pre-filled from the campaign (mergeWithParent), so sending it whole
        // would freeze inherited blocks as the line item's own. Line items keep the partial diff (pending, see plan).
        if (changed?.retargeting && !this._isStrategyContext) {
            changed.retargeting = cloneDeep(this.retargeting.getRawValue());
        }

        return changed;
    }

    private _computeChangedData(): any {
        if (this.originalFormData) {
            return this._stripUntouchedWipes(this.getChangedDataDirect());
        }

        if (!this.originalForm) {
            return this.getData();
        }

        const currentData = this.getData();
        const originalData = this.parseOriginalForm();

        return this._stripUntouchedWipes(this.getDeepDiff(originalData, currentData));
    }

    /**
     * Campos full-replace de `targeting` y el flag que autoriza vaciarlos.
     *
     * Espejo exacto de `PreserveTargetingListsAction::GUARDED_FIELDS` en la API. Si las dos
     * listas divergen pasa una de dos cosas, las dos malas: un campo que la API protege y el
     * front no confirma convierte cada borrado deliberado en un no-op silencioso, y un campo
     * que el front vacía sin que la API lo proteja vuelve a borrar targeting real en el DSP.
     */
    private static readonly GUARDED_TARGETING_FIELDS: Record<string, string> = {
        regions: "confirm_clear_geo",
        cities: "confirm_clear_geo",
        zipcodes: "confirm_clear_geo",
        publishers: "confirm_clear_publishers",
        blacklist_publishers: "confirm_clear_publishers",
        geolist_acquisition: "confirm_clear_geolist",
    };

    /**
     * Las tres formas que toma un control vaciado: `[]` en las listas de chips, `""` en los
     * selects de geolista y `null` cuando el modelo del formulario se resetea.
     */
    private _isClearedValue(value: any): boolean {
        return value === null || value === "" || (Array.isArray(value) && value.length === 0);
    }

    /**
     * Never emit an explicit wipe of a destructive full-replace field the user did not touch.
     * When the dictionary/catalog fails to load, chips may not render even though the control
     * still holds real values; a diff computed against a bad baseline then sends an empty value
     * and deletes real configuration in MediaSmart.
     * Covers geo targeting (regions/cities/zipcodes — dictionary incident), the publisher lists
     * and the geolists (triage of 2026-08-28), and deals_and_pricing.deals (Spotify deal
     * incident, 2026-07-20: an unhydrated deals selector wiped a PMP deal and the campaign
     * served open exchange).
     * `dirty` is only set by user interaction, never by patchValue(), so it is a safe signal.
     */
    private _stripUntouchedWipes(diff: any): any {
        if (!diff || typeof diff !== "object") {
            return diff;
        }

        if (diff.targeting && typeof diff.targeting === "object") {
            for (const [field, confirmFlag] of Object.entries(FormService.GUARDED_TARGETING_FIELDS)) {
                if (!(field in diff.targeting)) {
                    continue;
                }

                const control = this.targetingForm.get(field);
                if (!control || !this._isClearedValue(diff.targeting[field])) {
                    continue;
                }

                if (control.dirty) {
                    // The user actively emptied the selection — tell the API this is a
                    // deliberate clear so its anti-wipe guard lets it through (peeled off
                    // server-side, never reaches the DSP).
                    diff[confirmFlag] = true;
                } else {
                    delete diff.targeting[field];
                }
            }

            // Publisher fields are a different shape of the same problem: mergeWithParent shows
            // the campaign's list for display when the line item has none of its own (correct —
            // MediaSmart really does inherit that field live, H2-override,
            // INCIDENT_2026_07_14_BLACKLIST_PUBLISHERS_FORMAT.md), but `patchValue()` never marks
            // a control dirty. Saving any *other* field on this screen must not carry that
            // displayed-but-untouched value into the payload as if the trader had set it — that
            // would stamp the campaign's list onto the line item and permanently override what it
            // would otherwise have kept inheriting. Unlike the wipe guards above, this strips the
            // field whenever it's untouched, not only when it would wipe to empty: the dangerous
            // direction here is a *filled-in* merged value being mistaken for the trader's own.
            // Same exposure for the Geolista: `geolist_acquisition` has no dedicated guard in
            // mergeWithParent (it was never in the removed publisher-fields list either), so it
            // already falls back to the campaign's geolist through the generic branch — which is
            // why it shows up correctly in the line item's targeting form. Same fix needed here.
            for (const field of ["publishers", "blacklist_publishers", "use_publishers_as_blacklist", "publishers_rules", "geolist_acquisition"]) {
                const control = this.targetingForm.get(field);
                if (field in diff.targeting && control && !control.dirty) {
                    delete diff.targeting[field];
                }
            }

            if (Object.keys(diff.targeting).length === 0) {
                delete diff.targeting;
            }
        }

        if (diff.deals_and_pricing && typeof diff.deals_and_pricing === "object") {
            const deals = diff.deals_and_pricing.deals;
            const dealsControl = this.dealsForm.get("deals");
            if (Array.isArray(deals) && deals.length === 0 && dealsControl) {
                if (dealsControl.dirty) {
                    // The user actively emptied the chips — tell the API this
                    // is a deliberate clear so its anti-wipe guard lets it
                    // through (peeled off the payload server-side).
                    diff.confirm_clear_deals = true;
                } else {
                    delete diff.deals_and_pricing.deals;
                }
            }

            if (Object.keys(diff.deals_and_pricing).length === 0) {
                delete diff.deals_and_pricing;
            }
        }

        return diff;
    }

    private parseOriginalForm(): any {
        try {
            let stringForm = this.originalForm;
            stringForm = stringForm.replace(/\b1\b/g, '"__TRUE__"');
            stringForm = stringForm.replace(/\b0\b/g, '"__FALSE__"');
            stringForm = stringForm.replace(/""/g, "null");

            const parsed = JSON.parse(stringForm);
            return this.replacePlaceholders(parsed);
        } catch (error) {
            console.error("Error parsing original form:", error);
            return {};
        }
    }

    private replacePlaceholders(obj: any): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj === "string") {
            if (obj === "__TRUE__") return true;
            if (obj === "__FALSE__") return false;
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.replacePlaceholders(item));
        }

        if (typeof obj === "object") {
            const result: any = {};
            for (const key in obj) {
                result[key] = this.replacePlaceholders(obj[key]);
            }
            return result;
        }

        return obj;
    }

    private getDeepDiff(original: any, current: any, path: string = ""): any {
        const diff: any = {};

        if (current === null || current === undefined) {
            return original !== current ? current : undefined;
        }

        if (typeof current !== "object" || Array.isArray(current)) {
            return JSON.stringify(original) !== JSON.stringify(current) ? current : undefined;
        }

        // Si el original es null/undefined pero el current es un objeto, es un cambio válido
        if ((original === null || original === undefined) && typeof current === "object") {
            return current;
        }

        const allKeys = new Set([...Object.keys(original || {}), ...Object.keys(current || {})]);

        allKeys.forEach((key) => {
            const originalValue = original?.[key];
            const currentValue = current?.[key];

            // Para exchanges, si hay cambios, enviar el objeto completo (no solo el diff)
            // Esto asegura que se envíen todos los exchanges seleccionados, no solo los modificados
            if (key === "exchanges") {
                const originalStr = JSON.stringify(originalValue);
                const currentStr = JSON.stringify(currentValue);
                if (originalStr !== currentStr) {
                    diff[key] = currentValue;
                }
                return;
            }

            // Para goal, si hay cambios enviar el objeto completo {kpi, value}.
            // MediaSmart procesa goal como atómico; mandar solo value sin kpi (o viceversa) deja
            // el campo incompleto al persistirse en BD y MediaSmart no aplica el cambio.
            if (key === "goal") {
                const originalStr = JSON.stringify(originalValue);
                const currentStr = JSON.stringify(currentValue);
                if (originalStr !== currentStr) {
                    diff[key] = currentValue;
                }
                return;
            }

            // Para publisher_type, si hay cambios, enviar el objeto completo.
            // En la práctica, el backend espera el objeto completo (web/app) y si solo enviamos el delta
            // (ej: { app: true } al pasar de web->All), termina sobreescribiendo y perdiendo el otro flag.
            if (key === "publisher_type") {
                const originalStr = JSON.stringify(originalValue);
                const currentStr = JSON.stringify(currentValue);
                if (originalStr !== currentStr) {
                    const normalized: Record<string, boolean> = {};
                    if (currentValue && typeof currentValue === "object") {
                        const cv = currentValue as Record<string, unknown>;
                        if (cv.web === true) normalized.web = true;
                        if (cv.app === true) normalized.app = true;
                    }
                    diff[key] = normalized;
                }
                return;
            }

            if (currentValue && typeof currentValue === "object" && !Array.isArray(currentValue)) {
                // Si el original es null/undefined pero el current es un objeto, es un cambio
                if (originalValue === null || originalValue === undefined) {
                    diff[key] = currentValue;
                } else {
                    const nestedDiff = this.getDeepDiff(originalValue, currentValue, `${path}${key}.`);
                    if (nestedDiff !== undefined) {
                        diff[key] = nestedDiff;
                    }
                }
            } else {
                const originalStr = JSON.stringify(originalValue);
                const currentStr = JSON.stringify(currentValue);

                if (originalStr !== currentStr) {
                    diff[key] = currentValue;
                }
            }
        });

        return Object.keys(diff).length > 0 ? diff : undefined;
    }

    public mergeWithParent(campaign: any, strategy: any): any {
        if (!campaign) return strategy || {};
        if (!strategy) return campaign || {};

        // Frequency limits fields that should allow empty values in strategies
        const frequencyLimitFields = [
            "max_impressions_user",
            "max_impressions_user_day",
            "max_impressions_user_hour",
            "max_adviewed_user",
            "max_adviewed_user_day",
            "max_adviewed_user_hour",
        ];

        /**
         * A line item's budget figures are its own and must never be inherited from the parent
         * campaign — same reasoning as the deals guard below: an inherited figure reads as the line
         * item's own value.
         *
         * For the LIFETIME fields it matters twice over: inheriting it would pre-fill the input with
         * the campaign's number without the trader deciding anything and then save an empty diff —
         * leaving the line item with no budget of its own while the screen claimed otherwise — and an
         * empty lifetime is precisely what says the line item runs in auto.
         *
         * The DAILY caps are here for a second reason on top of that one. While a line item still sent
         * `daily_limits_type` (until 28/09/2026) it was derived from these fields, so a cap inherited
         * from the campaign (1000 €/day on the parent, nothing on the line item) made every save claim
         * "manual" while carrying no cap at all — telling the DSP to honour limits the trader never set.
         * The key no longer travels (see the note on `daily_limits_type` in strategy-budget.utils), but
         * an inherited cap would still be saved as the line item's own the moment the form is submitted.
         */
        const strategyOwnedBudgetFields = [
            "max_global_cost",
            "max_global_impressions",
            "max_global_clicks",
            "max_daily_cost",
            "max_daily_impressions",
            "max_daily_clicks",
        ];

        // Recursive function to perform deep merge
        const deepMerge = (campaign: any, strategy: any, parentKey?: string): any => {
            const result: any = { ...campaign, ...strategy }; // Primero copiamos campaign y luego strategy sobrescribe

            for (const key of Object.keys(campaign)) {
                const obj1Value = campaign[key];
                const obj2Value = strategy[key];

                if (
                    typeof obj1Value === "object" &&
                    typeof obj2Value === "object" &&
                    !Array.isArray(obj1Value) &&
                    obj1Value !== null &&
                    obj2Value !== null
                ) {
                    result[key] = deepMerge(obj1Value, obj2Value, key);
                } else if (parentKey === "deals_and_pricing" && key === "deals") {
                    // A line item's deal list is its own — never inherit the
                    // parent campaign's deals into the strategy selector. The
                    // inherited chips read as the line item's deals; a user
                    // "cleaning them up" wipes the real PMP deals in the DSP
                    // (Spotify incident, 2026-07-20). An empty [] on the
                    // strategy side is a real value, not an absent one.
                    if (obj2Value === undefined || obj2Value === null) {
                        delete result[key];
                    } else {
                        result[key] = obj2Value;
                    }
                } else if (parentKey === "targeting" && (key === "publishers" || key === "blacklist_publishers")) {
                    // In MediaSmart an empty [] on a line item is its OWN empty list ("block/include
                    // nothing") and stops inheriting; only null (or no field) inherits the campaign's
                    // list, live (measured in pre 25/09/2026, ticket herencia campaña→LI). Treating []
                    // as "absent" painted the campaign's list right after the trader emptied the line
                    // item's — and only on the post-save refresh: after F5 the targeting tab's own
                    // strategy$ patch wins and shows 0 (pre, 05/11/2026, LI 37qtp4kz).
                    result[key] = obj2Value === undefined || obj2Value === null ? obj1Value : obj2Value;
                } else if (
                    obj2Value === null ||
                    obj2Value === undefined ||
                    (typeof obj2Value === "string" && obj2Value.trim() === "") ||
                    (Array.isArray(obj2Value) && obj2Value.length === 0) ||
                    (typeof obj2Value === "object" && Object.keys(obj2Value).length === 0) // Si es objeto vacío
                ) {
                    // Special handling for frequency limit fields in schedule
                    if (parentKey === "schedule" && frequencyLimitFields.includes(key)) {
                        // For frequency limits, preserve empty values from strategy
                        result[key] = obj2Value;
                    } else if (parentKey === "schedule" && strategyOwnedBudgetFields.includes(key)) {
                        // Keep the line item's own (empty) value instead of the campaign's. Normalised
                        // to null rather than left undefined so the control renders empty and the
                        // required validator actually fires.
                        result[key] = obj2Value ?? null;
                    } else {
                        result[key] = obj1Value;
                    }
                } else {
                    result[key] = obj2Value;
                }
            }

            return result;
        };

        const mergedResult = deepMerge(campaign, strategy);

        delete mergedResult.creatives; // Elimina el campo "creatives" antes de retornar

        return mergedResult;
    }
}
